import { anyLeague } from "../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../entities/index.js";
import { BattingRankingStats, DataSection, PitchData, ResultData, ResultType, StatType } from "../../enums/index.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import resultsSliceForPitchType from "../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import battingScopeContext from "../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import sessionWithOldTaggingData from "../../classificators/session/sessionWithOldTaggingData.js";
import ClassifiedPitchesFilter from "../../filters/contextual/ClassifiedPitchesFilter.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";
import defaultReachedOnErrorClassifier from '../../classificators/reachedOnErrorClassifier.js';
import RankingStatCalculatorBase from "./RankingStatCalculatorBase.js";

class ReachedOnErrorCalculator extends RankingStatCalculatorBase {
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter, contextualResultFilter,
        reachedOnErrorClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (typeof(reachedOnErrorClassifier) !== 'undefined' && typeof(reachedOnErrorClassifier) !== 'function') {
            throw new TypeError('reachedOnErrorClassifier must be an function or undefined');
        }

        super(
            [ BattingRankingStats.ReachedOnError ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithOldTaggingData,
                satisfiesAll(
                    battingScopeContext,
                    resultsSliceForPitchType
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.reachedOnErrorClassifier = reachedOnErrorClassifier || defaultReachedOnErrorClassifier;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        // TODO - for performance reasons this could be skipped here
        const pitches = this.filterResults(slice.getSmallestSlice(), session, league);

        // TODO - for performance reasons we could skip grouping here and simply iterate over results
        const stats = this.resultsProcessor.calcStats(
            [StatType.Count.key],
            pitches,
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(PitchData.Result.key, DataSection.Tagging.key, ResultType.Pitch.key)
            ) ],
            false
        );

        let roe = 0;
        if (!stats || !stats.hasOwnProperty(PitchData.Result.key) || !stats[PitchData.Result.key][StatType.Ratio.key]){
            return null;
        }

        for (const result in stats[PitchData.Result.key][StatType.Ratio.key]) {
            if (!Object.hasOwnProperty.call(stats[PitchData.Result.key][StatType.Ratio.key], result)) {
                continue;
            }

            const count = stats[PitchData.Result.key][StatType.Ratio.key][result].count;
            // https://www.mlb.com/glossary/standard-stats/reached-on-error
            // A batter receives a reached on error when he reaches base because of a defensive error -- meaning he wouldn't have otherwise reached.
            if (this.reachedOnErrorClassifier(result)) {
                roe += count;
            }
        }

        // now include roe and total bases
        if (roe > 0) {
            const reachedOnError = this.supportedParams[0];
            return RankingStat.fromParameter(
                reachedOnError, this.numberFormatter.format(roe), roe
            );
        }

        return null;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default ReachedOnErrorCalculator;